import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Button, message, Tabs, Space, Descriptions, InputNumber, Modal, Form, Input, Popconfirm, DatePicker, Select, Row, Col } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { collaboratorAPI, invoiceAPI } from '../services/api';

const CollaboratorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [collaborator, setCollaborator] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [depositModalVisible, setDepositModalVisible] = useState(false);
  const [editingDeposit, setEditingDeposit] = useState(null);
  const [editingInvoiceData, setEditingInvoiceData] = useState({});
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [dateFilter, setDateFilter] = useState([null, null]);
  const [noteFilter, setNoteFilter] = useState('');
  const [noteFilterType, setNoteFilterType] = useState('contains');
  const [form] = Form.useForm();

  // Check if user is a collaborator (read-only mode)
  const userRole = JSON.parse(localStorage.getItem('user') || '{}').role;
  const isCollaborator = userRole === 'collaborator';

  useEffect(() => {
    fetchCollaboratorDetail();
  }, [id]);

  const fetchCollaboratorDetail = async () => {
    setLoading(true);
    try {
      const response = await collaboratorAPI.getById(id);
      setCollaborator(response.data.collaborator);
      setInvoices(response.data.invoices);
    } catch (error) {
      message.error('Lỗi khi tải chi tiết cộng tác viên');
    }
    setLoading(false);
  };

  const handleAddDeposit = async (values) => {
    try {
      if (editingDeposit) {
        await collaboratorAPI.updateDeposit(id, editingDeposit._id, values);
        message.success('Cập nhật tiền nạp thành công');
      } else {
        await collaboratorAPI.addDeposit(id, values);
        message.success('Nạp tiền thành công');
      }
      setDepositModalVisible(false);
      setEditingDeposit(null);
      form.resetFields();
      fetchCollaboratorDetail();
    } catch (error) {
      message.error('Lỗi khi lưu dữ liệu');
    }
  };

  const handleEditDeposit = (record) => {
    setEditingDeposit(record);
    form.setFieldsValue({
      amount: record.amount,
      note: record.note
    });
    setDepositModalVisible(true);
  };

  const handleDeleteDeposit = async (depositId) => {
    try {
      await collaboratorAPI.deleteDeposit(id, depositId);
      message.success('Xóa lịch sử nạp tiền thành công');
      fetchCollaboratorDetail();
    } catch (error) {
      message.error('Lỗi khi xóa lịch sử nạp tiền');
    }
  };

  const handleInvoiceCellEdit = (record, field, value) => {
    setEditingInvoiceData(prev => ({
      ...prev,
      [record._id]: {
        ...prev[record._id],
        [field]: value
      }
    }));
  };

  const handleEditInvoice = (record) => {
    if (!record.invoiceId.isActive) {
      message.error('Invoice đã bị khóa, không thể chỉnh sửa');
      return;
    }
    setEditingInvoiceId(record._id);
    setEditingInvoiceData({
      [record._id]: {
        appSpentCtv: record.appSpentCtv,
        vpcsSpentCtv: record.vpcsSpentCtv,
        appCttCtv: record.appCttCtv,
        vpcsCttCtv: record.vpcsCttCtv
      }
    });
  };

  const handleSaveInvoice = async (record) => {
    try {
      const updatedData = editingInvoiceData[record._id] || {};
      const response = await invoiceAPI.updateCollaborator(record._id, {
        appSpentCtv: updatedData.appSpentCtv ?? record.appSpentCtv,
        vpcsSpentCtv: updatedData.vpcsSpentCtv ?? record.vpcsSpentCtv,
        appCttCtv: updatedData.appCttCtv ?? record.appCttCtv,
        vpcsCttCtv: updatedData.vpcsCttCtv ?? record.vpcsCttCtv
      });
      message.success('Lưu thành công - Funds đã được cập nhật tự động');
      setEditingInvoiceId(null);
      setEditingInvoiceData({});
      // Refresh để lấy funds mới
      await fetchCollaboratorDetail();
    } catch (error) {
      console.error('Lỗi khi lưu:', error);
      message.error(error.response?.data?.error || 'Lỗi khi lưu dữ liệu');
    }
  };

  const handleCancelEdit = () => {
    setEditingInvoiceId(null);
    setEditingInvoiceData({});
  };

  const getFilteredDeposits = () => {
    if (!collaborator?.deposits) return [];

    let filtered = [...collaborator.deposits];

    // Date filter
    if (dateFilter[0] && dateFilter[1]) {
      filtered = filtered.filter(deposit => {
        const depositDate = new Date(deposit.createdAt);
        return depositDate >= dateFilter[0].startOf('day').toDate() &&
          depositDate <= dateFilter[1].endOf('day').toDate();
      });
    }

    // Note filter
    if (noteFilter) {
      filtered = filtered.filter(deposit => {
        const note = deposit.note || '';
        switch (noteFilterType) {
          case 'contains':
            return note.toLowerCase().includes(noteFilter.toLowerCase());
          case 'equals':
            return note.toLowerCase() === noteFilter.toLowerCase();
          case 'notContains':
            return !note.toLowerCase().includes(noteFilter.toLowerCase());
          case 'notEquals':
            return note.toLowerCase() !== noteFilter.toLowerCase();
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  const EditableInvoiceCell = ({ record, field }) => {
    const isEditing = editingInvoiceId === record._id && !isCollaborator;
    const isLocked = !record.invoiceId.isActive;
    const value = editingInvoiceData[record._id]?.[field] ?? record[field];

    if (isEditing && !isLocked) {
      return (
        <Input
          value={value}
          onChange={(e) => handleInvoiceCellEdit(record, field, parseFloat(e.target.value) || 0)}
          style={{ width: '100%' }}
          placeholder="Nhập số"
        />
      );
    }

    return (
      <span style={{ color: isLocked ? '#999' : 'inherit' }}>
        ${value?.toLocaleString()}
      </span>
    );
  };

  const depositColumns = [
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (val) => `$${val?.toLocaleString()}`
    },
    {
      title: 'Ngày giờ',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val) => new Date(val).toLocaleString('vi-VN')
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note'
    },
    ...(!isCollaborator ? [{
      title: 'Hành động',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditDeposit(record)} />
          <Popconfirm
            title="Xác nhận xóa?"
            onConfirm={() => handleDeleteDeposit(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }] : [])
  ];

  const invoiceColumns = [
    {
      title: 'Mã Invoice',
      dataIndex: ['invoiceId', 'code'],
      key: 'invoiceCode',
      fixed: 'left',
      width: 150,
      render: (text, record) => (
        <Space>
          <span>{text}</span>
          {!record.invoiceId.isActive && (
            <span style={{ color: 'red', fontSize: '12px' }}>(Đã khóa)</span>
          )}
        </Space>
      )
    },
    {
      title: 'APP Tiêu thụ',
      dataIndex: 'appSpentCtv',
      key: 'appSpentCtv',
      width: 150,
      render: (_, record) => <EditableInvoiceCell record={record} field="appSpentCtv" />
    },
    {
      title: 'VPCS Tiêu thụ',
      dataIndex: 'vpcsSpentCtv',
      key: 'vpcsSpentCtv',
      width: 150,
      render: (_, record) => <EditableInvoiceCell record={record} field="vpcsSpentCtv" />
    },
    {
      title: 'APP Chi tiêu trước',
      dataIndex: 'appCttCtv',
      key: 'appCttCtv',
      width: 150,
      render: (_, record) => <EditableInvoiceCell record={record} field="appCttCtv" />
    },
    {
      title: 'VPCS Chi tiêu trước',
      dataIndex: 'vpcsCttCtv',
      key: 'vpcsCttCtv',
      width: 150,
      render: (_, record) => <EditableInvoiceCell record={record} field="vpcsCttCtv" />
    },
    {
      title: 'Tổng tiêu thụ',
      dataIndex: 'totalSpentCtv',
      key: 'totalSpentCtv',
      width: 130,
      render: (val) => `$${val?.toLocaleString()}`
    },
    {
      title: 'Rate APP',
      dataIndex: 'rateAppSpentCtv',
      key: 'rateAppSpentCtv',
      width: 120,
      render: (val) => `$${val?.toLocaleString()}`
    },
    {
      title: 'Rate VPCS',
      dataIndex: 'rateVpcsSpentCtv',
      key: 'rateVpcsSpentCtv',
      width: 120,
      render: (val) => `$${val?.toLocaleString()}`
    },
    ...(!isCollaborator ? [{
      title: 'Hành động',
      key: 'action',
      fixed: 'right',
      width: 220,
      render: (_, record) => {
        const isEditing = editingInvoiceId === record._id;
        const isLocked = !record.invoiceId.isActive;

        return (
          <Space>
            {!isLocked && (
              isEditing ? (
                <>
                  <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    onClick={() => handleSaveInvoice(record)}
                  >
                    Lưu
                  </Button>
                  <Button
                    size="small"
                    onClick={handleCancelEdit}
                  >
                    Hủy
                  </Button>
                </>
              ) : (
                <Button
                  type="default"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEditInvoice(record)}
                >
                  Edit
                </Button>
              )
            )}
            <Button
              type="link"
              size="small"
              onClick={() => navigate(`/invoices/${record.invoiceId._id}`)}
            >
              Xem Invoice
            </Button>
          </Space>
        );
      }
    }] : [])
  ];

  if (!collaborator) return <div style={{ padding: 24 }}>Đang tải...</div>;

  const totalRateSpent = invoices.reduce((sum, inv) => sum + (inv.totalRateSpentCtv || 0), 0);
  const totalCttCtv = invoices.reduce((sum, inv) => sum + ((inv.appCttCtv || 0) + (inv.vpcsCttCtv || 0)), 0);
  const totalSpent = invoices.reduce((sum, inv) => sum + (inv.totalSpentCtv || 0), 0) - totalCttCtv;
  const totalRateAppSpent = invoices.reduce((sum, inv) => sum + (inv.rateAppSpentCtv || 0), 0);
  const totalRateVpcsSpent = invoices.reduce((sum, inv) => sum + (inv.rateVpcsSpentCtv || 0), 0);
  const appSpentCtv = invoices.reduce((sum, inv) => sum + (inv.appSpentCtv || 0), 0);
  const vpcsSpentCtv = invoices.reduce((sum, inv) => sum + (inv.vpcsSpentCtv || 0), 0);
  
  const items = [
    {
      key: '1',
      label: 'Thông tin CTV',
      children: (
        <Card>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Telegram">{collaborator.tele}</Descriptions.Item>
            <Descriptions.Item label="Ngày tạo">
              {new Date(collaborator.createdAt).toLocaleDateString('vi-VN')}
            </Descriptions.Item>
            <Descriptions.Item label="Tổng nạp" span={2}>
              <span style={{ fontSize: 16, fontWeight: 'bold' }}>
                ${collaborator.totalDeposit?.toLocaleString()}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Invoice Tham gia" span={2}>
              <span style={{ fontSize: 14 }}>
                {invoices
                  .filter(inv => inv.invoiceId.isActive)
                  .map(inv => inv.invoiceId.code)
                  .join('- ') || 'Không có'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Tổng tiêu thụ" span={2}>
              <span style={{ fontSize: 16 }}>
                ${totalSpent.toLocaleString()} ( App: {appSpentCtv.toLocaleString()} + VPCS: {vpcsSpentCtv.toLocaleString()} )
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Tổng tiêu thụ Sau Rate" span={2}>
              <span style={{ fontSize: 16 }}>
                ${totalRateSpent.toLocaleString()} ( App: {totalRateAppSpent.toLocaleString()} + VPCS: {totalRateVpcsSpent.toLocaleString()} )
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Số dư" span={2}>
              <span
                style={{
                  color: collaborator.funds < 0 ? 'red' : 'green',
                  fontWeight: 'bold',
                  fontSize: 18,
                }}
              >
                ${collaborator.funds?.toLocaleString()}
              </span>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )
    },
    {
      key: '2',
      label: 'Lịch sử nạp tiền',
      children: (
        <div>
          <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Space>
                  <span>Lọc theo ngày:</span>
                  <DatePicker.RangePicker
                    value={dateFilter}
                    onChange={(dates) => setDateFilter(dates || [null, null])}
                    format="DD/MM/YYYY"
                    placeholder={['Từ ngày', 'Đến ngày']}
                  />
                </Space>
              </Col>
              <Col span={12}>
                <Space>
                  <span>Lọc ghi chú:</span>
                  <Select
                    value={noteFilterType}
                    onChange={setNoteFilterType}
                    style={{ width: 150 }}
                  >
                    <Select.Option value="contains">Chứa</Select.Option>
                    <Select.Option value="equals">Bằng</Select.Option>
                    <Select.Option value="notContains">Không chứa</Select.Option>
                    <Select.Option value="notEquals">Không bằng</Select.Option>
                  </Select>
                  <Input
                    value={noteFilter}
                    onChange={(e) => setNoteFilter(e.target.value)}
                    placeholder="Nhập ghi chú"
                    style={{ width: 200 }}
                    allowClear
                  />
                </Space>
              </Col>
            </Row>
            {!isCollaborator && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingDeposit(null);
                  form.resetFields();
                  setDepositModalVisible(true);
                }}
              >
                Nạp tiền
              </Button>
            )}
          </Space>
          <Table
            columns={depositColumns}
            dataSource={getFilteredDeposits()}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
            summary={(pageData) => {
              const total = pageData.reduce((sum, record) => sum + (record.amount || 0), 0);
              return (
                <Table.Summary.Row style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                  <Table.Summary.Cell index={0}>Tổng</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>${total.toLocaleString()}</Table.Summary.Cell>
                  <Table.Summary.Cell index={2} colSpan={2} />
                </Table.Summary.Row>
              );
            }}
          />
        </div>
      )
    },
    {
      key: '3',
      label: 'Lịch sử Invoice',
      children: (
        <Table
          columns={invoiceColumns}
          dataSource={invoices}
          loading={loading}
          rowKey="_id"
          scroll={{ x: 1800 }}
          pagination={{ pageSize: 10 }}
          title={() => <strong>Bảng chi tiêu của CTV này trong các Invoice (có thể chỉnh sửa)</strong>}
          summary={(pageData) => {
            let totalApp = 0, totalVpcs = 0, totalAppCtt = 0, totalVpcsCtt = 0;
            let totalSpent = 0;
            let totalRateApp = 0, totalRateVpcs = 0;

            pageData.forEach(({ appSpentCtv, vpcsSpentCtv, appCttCtv, vpcsCttCtv, totalSpentCtv,
              rateAppSpentCtv, rateVpcsSpentCtv }) => {
              totalApp += appSpentCtv || 0;
              totalVpcs += vpcsSpentCtv || 0;
              totalAppCtt += appCttCtv || 0;
              totalVpcsCtt += vpcsCttCtv || 0;
              totalSpent += totalSpentCtv || 0;
              totalRateApp += rateAppSpentCtv || 0;
              totalRateVpcs += rateVpcsSpentCtv || 0;
            });

            return (
              <Table.Summary.Row style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                <Table.Summary.Cell index={0}>Tổng cộng</Table.Summary.Cell>
                <Table.Summary.Cell index={1}>${totalApp.toLocaleString()}</Table.Summary.Cell>
                <Table.Summary.Cell index={2}>${totalVpcs.toLocaleString()}</Table.Summary.Cell>
                <Table.Summary.Cell index={3}>${totalAppCtt.toLocaleString()}</Table.Summary.Cell>
                <Table.Summary.Cell index={4}>${totalVpcsCtt.toLocaleString()}</Table.Summary.Cell>
                <Table.Summary.Cell index={5}>${totalSpent.toLocaleString()}</Table.Summary.Cell>
                <Table.Summary.Cell index={6}>${totalRateApp.toLocaleString()}</Table.Summary.Cell>
                <Table.Summary.Cell index={7}>${totalRateVpcs.toLocaleString()}</Table.Summary.Cell>
                <Table.Summary.Cell index={8} />
              </Table.Summary.Row>
            );
          }}
        />
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        {!isCollaborator && (
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/collaborators')}>
            Quay lại
          </Button>
        )}
        <h1 style={{ margin: 0 }}>{collaborator.tele}</h1>
      </Space>

      <Tabs items={items} />

      <Modal
        title={editingDeposit ? 'Sửa tiền nạp' : 'Nạp tiền'}
        open={depositModalVisible}
        onCancel={() => {
          setDepositModalVisible(false);
          setEditingDeposit(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} onFinish={handleAddDeposit} layout="vertical">
          <Form.Item
            name="amount"
            label="Số tiền"
            rules={[{ required: true, message: 'Vui lòng nhập số tiền' }]}
          >
            <Input
              style={{ width: '100%' }}
              placeholder="Nhập số tiền"
            />
          </Form.Item>
          <Form.Item name="note" label="Ghi chú">
            <Input.TextArea rows={3} placeholder="Ghi chú (không bắt buộc)" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            {editingDeposit ? 'Cập nhật' : 'Nạp tiền'}
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default CollaboratorDetail;
